import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Check, Copy, Download, ImageIcon, Share2 } from 'lucide-react';
import { Modal } from '../../Modal';
import { Button, Card, SocialLogo, Typography } from '../ui';
import { ConnectWhatsappIcon } from '../sidebar/components/nav-icons';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useNarrow } from '../../../lib/use-narrow';
import { useProductName } from '../../../lib/use-brand';
import { shareIntentUrl } from '../../../lib/wins';
import {
  NO_IMAGE_CAPABILITIES,
  detectShareImageCapabilities,
  isShareCancel,
  linkTargetDownloadsImage,
  shareImageActions,
  shareImageFileName,
  showLinkTargets,
  targetHintKeys,
  type ShareImageCapabilities,
  type ShareTarget,
} from '../../../lib/win-share-image';
import styles from './ShareWinDialog.module.css';

export interface ShareRequest {
  /** The dialog title — a single win or a recap. */
  title: string;
  caption: string;
  preview: ReactNode;
  /** For BI: a win id, or `recap-week` / `recap-month`. */
  entity: string;
  /** Where the shared card sat on the page: the preview flies in from there. */
  from?: DOMRect;
}

const FLY_MS = 430;

type Target = ShareTarget;

// Brand names are proper nouns and read the same in every locale.
const TARGETS: { id: Target; label?: string; icon: ReactNode }[] = [
  { id: 'instagram', label: 'Instagram', icon: <SocialLogo network="INSTAGRAM" size={22} /> },
  { id: 'linkedin', label: 'LinkedIn', icon: <SocialLogo network="LINKEDIN" size={22} /> },
  { id: 'facebook', label: 'Facebook', icon: <SocialLogo network="FACEBOOK" size={22} /> },
  { id: 'x', label: 'X', icon: <SocialLogo network="X" size={20} /> },
  { id: 'whatsapp', label: 'WhatsApp', icon: <ConnectWhatsappIcon /> },
  { id: 'copy', icon: <Copy size={20} aria-hidden /> },
];

/** The product's public home, for the one network (Facebook) whose share intent takes only a link. */
const PRODUCT_URL = 'https://symphony.wix.com';

type CardImage =
  | { status: 'idle' | 'rendering' | 'failed' }
  | { status: 'ready'; blob: Blob; file: File };

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Draws the preview card to a PNG. html-to-image clones the node with its COMPUTED styles inlined, so the
 * `--ds-*` / tone custom properties arrive already resolved (and in the current light or dark values), and
 * it embeds the @font-face rules the card uses as data URLs, so the brand faces survive the SVG round trip.
 * It runs twice: WebKit decodes the embedded fonts and images lazily inside the SVG `<img>`, and the first
 * draw there can come out without them.
 */
async function renderCardPng(node: HTMLElement): Promise<Blob | null> {
  const { toBlob } = await import('html-to-image');
  if (typeof document !== 'undefined' && document.fonts) await document.fonts.ready;
  const options = { pixelRatio: 3, cacheBust: false };
  await toBlob(node, options);
  return toBlob(node, options);
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * "Share this win": the card as it will look, an editable caption, and the networks. Every network
 * with a public share intent is a real link (`target="_blank"`), because many in-app browsers block
 * `window.open`. Instagram has no web intent and Facebook's takes only a link, so those two copy the
 * caption and say so. There is no public page for a win, so the last target copies the caption rather
 * than pretending to copy a link.
 *
 * The card itself travels as a PNG drawn from the preview when the dialog opens. A share-intent URL can
 * carry text only, so the image reaches a post one of three ways (`lib/win-share-image.ts`): the system
 * share sheet with the file attached where the browser supports it, otherwise a download (which every link
 * target also triggers) or the clipboard, with a line that says to attach it.
 */
export function ShareWinDialog({ request, onClose }: { request: ShareRequest | null; onClose: () => void }) {
  const { t } = useTranslation();
  const narrow = useNarrow();
  const { logModalActionBi } = useGenericBi();
  const productName = useProductName();
  const [caption, setCaption] = useState('');
  const [done, setDone] = useState<Target | 'copy-image' | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [image, setImage] = useState<CardImage>({ status: 'idle' });
  const [caps, setCaps] = useState<ShareImageCapabilities>(NO_IMAGE_CAPABILITIES);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!request) return;
    setCaption(request.caption);
    setDone(null);
    setHint(null);
    setImage({ status: 'idle' });
    setCaps(NO_IMAGE_CAPABILITIES);
  }, [request]);

  // The flight moves the preview's wrapper, never the card inside it, which is the node the PNG is drawn
  // from: a transform caught mid-flight would otherwise be baked into the shared image.
  useEffect(() => {
    const from = request?.from;
    if (!from || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const frame = requestAnimationFrame(() => {
      const el = previewRef.current;
      if (!el || typeof el.animate !== 'function') return;
      const to = el.getBoundingClientRect();
      if (!to.width) return;
      el.style.transformOrigin = 'top left';
      el.animate(
        [
          { transform: `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${from.width / to.width})` },
          { transform: 'none' },
        ],
        { duration: FLY_MS, easing: 'cubic-bezier(.22,.7,.23,1)' },
      );
    });
    return () => cancelAnimationFrame(frame);
  }, [request]);

  // Drawn ahead of any click: `navigator.share` and `clipboard.write` must run inside the user's gesture,
  // and Safari drops a gesture that waited on a render.
  useEffect(() => {
    if (!request) return;
    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      const node = previewRef.current?.firstElementChild;
      if (!(node instanceof HTMLElement)) {
        setImage({ status: 'failed' });
        return;
      }
      setImage({ status: 'rendering' });
      renderCardPng(node)
        .then((blob) => {
          if (cancelled) return;
          if (!blob) {
            setImage({ status: 'failed' });
            return;
          }
          const file = new File([blob], shareImageFileName(request.entity, productName, new Date()), { type: 'image/png' });
          const Item = typeof ClipboardItem === 'undefined' ? undefined : ClipboardItem;
          const supports = (Item as unknown as { supports?: (type: string) => boolean } | undefined)?.supports;
          setCaps(
            detectShareImageCapabilities({
              navigator,
              file,
              hasClipboardItem: !!Item,
              pngClipboard: supports ? supports.call(Item, 'image/png') : undefined,
            }),
          );
          setImage({ status: 'ready', blob, file });
        })
        .catch(() => {
          if (!cancelled) setImage({ status: 'failed' });
        });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [request, productName]);

  const report = (action: string) =>
    logModalActionBi({ page_name: 'wins_page', modal_name: 'share_win', action }, { componentName: request?.entity });

  const ready = image.status === 'ready' ? image : null;
  const downloadsWithLinks = linkTargetDownloadsImage(caps, !!ready);
  const actions = shareImageActions(caps);

  const showHints = (keys: string[]) => setHint(keys.length ? keys.map((k) => t(k)).join(' ') : null);

  const copyFor = async (target: Target) => {
    const ok = await copyText(caption);
    setDone(ok ? target : null);
    if (ok) showHints(targetHintKeys(target, downloadsWithLinks));
  };

  const downloadImage = () => {
    if (!ready) return;
    downloadBlob(ready.blob, ready.file.name);
  };

  const shareNative = async () => {
    if (!ready) return;
    report('share_image');
    // Facebook and Instagram drop the text a web share hands them, so the caption also goes to the clipboard.
    // Not awaited: an await here would spend the tap Safari requires `navigator.share` to run inside.
    void copyText(caption);
    try {
      await navigator.share({ files: [ready.file], text: caption });
    } catch (error) {
      if (!isShareCancel(error)) setHint(t('wins.share.imageFailed'));
    }
  };

  const copyImage = async () => {
    if (!ready) return;
    report('copy_image');
    try {
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': ready.blob })]);
      setDone('copy-image');
      setHint(null);
    } catch {
      setDone(null);
      setHint(t('wins.share.imageFailed'));
    }
  };

  const imageNote =
    image.status === 'failed' ? t('wins.share.imageFailed') : caps.canShareFiles ? t('wins.share.nativeNote') : t('wins.share.imageNote');

  return (
    <Modal
      open={!!request}
      onClose={() => {
        logModalActionBi({ page_name: 'wins_page', modal_name: 'share_win', action: 'close' });
        onClose();
      }}
      title={request?.title}
      description={t('wins.share.description')}
      width={720}
      maxHeightPx={650}
      scrollBody
    >
      <div className={styles.body}>
        <div className={styles.preview} ref={previewRef}>
          {request?.preview}
        </div>
        <div className={styles.side}>
          <label className={`sym-type-tiny-medium ${styles.lbl}`} htmlFor="wins-share-caption">
            {t('wins.share.caption')}
          </label>
          <textarea id="wins-share-caption" className={styles.caption} rows={5} value={caption} onChange={(e) => setCaption(e.target.value)} />
          <div className={styles.imageActions} role="group" aria-label={t('wins.share.imageActions')}>
            {actions.map((action) => {
              const disabled = !ready;
              if (action === 'share') {
                return (
                  <Button key={action} size="small" startIcon={<Share2 size={16} aria-hidden />} disabled={disabled} onClick={() => void shareNative()}>
                    {t('wins.share.nativeShare')}
                  </Button>
                );
              }
              if (action === 'download') {
                return (
                  <Button
                    key={action}
                    size="small"
                    variant={caps.canShareFiles ? 'outlined' : 'contained'}
                    startIcon={<Download size={16} aria-hidden />}
                    loading={image.status === 'idle' || image.status === 'rendering'}
                    disabled={disabled}
                    onClick={() => {
                      report('download_image');
                      downloadImage();
                      showHints(['wins.share.imageDownloadedHint']);
                    }}
                  >
                    {t('wins.share.downloadImage')}
                  </Button>
                );
              }
              const copied = done === 'copy-image';
              return (
                <Button
                  key={action}
                  size="small"
                  variant="outlined"
                  startIcon={copied ? <Check size={16} aria-hidden /> : <ImageIcon size={16} aria-hidden />}
                  disabled={disabled}
                  onClick={() => void copyImage()}
                >
                  {t(copied ? 'wins.share.imageCopied' : 'wins.share.copyImage')}
                </Button>
              );
            })}
          </div>
          <Typography variant="p3" color="secondary" className={styles.hint}>
            {imageNote}
          </Typography>
          {hint && (
            <Typography variant="p3" color="secondary" role="status" className={styles.hint}>
              {hint}
            </Typography>
          )}
        </div>
      </div>
      {showLinkTargets(caps, narrow) && (
      <div className={styles.targets} role="group" aria-label={t('wins.share.targets')}>
        {TARGETS.map(({ id, label, icon }) => {
          const isDone = done === id;
          const text = id === 'copy' ? t(isDone ? 'wins.share.copied' : 'wins.share.copyCaption') : label;
          const inner = (
            <>
              <span className={styles.tile}>{isDone && id === 'copy' ? <Check size={20} aria-hidden /> : icon}</span>
              <span className={`sym-type-tiny-medium ${styles.targetLabel}`} data-done={isDone ? '' : undefined}>
                {text}
              </span>
            </>
          );
          if (id === 'instagram' || id === 'copy') {
            return (
              <Card
                key={id}
                component="button"
                variant="outline"
                size="sm"
                interactive
                className={styles.target}
                onClick={() => {
                  report(id);
                  if (id === 'instagram' && downloadsWithLinks) downloadImage();
                  void copyFor(id);
                }}
              >
                {inner}
              </Card>
            );
          }
          return (
            <Card
              key={id}
              component="a"
              variant="outline"
              size="sm"
              interactive
              className={styles.target}
              {...({ href: shareIntentUrl(id, caption, PRODUCT_URL), target: '_blank', rel: 'noopener noreferrer' } as Record<string, string>)}
              onClick={() => {
                report(id);
                if (downloadsWithLinks) downloadImage();
                if (id === 'facebook') void copyFor(id);
                else showHints(targetHintKeys(id, downloadsWithLinks));
              }}
            >
              {inner}
            </Card>
          );
        })}
      </div>
      )}
    </Modal>
  );
}
